/**
 * Look Deeper Modal - Unified
 *
 * Observer level: Shows interactive timeline with historical civic moments
 * Other levels: Block-based content renderer
 */

import React, { useEffect, useState } from 'react';
import { Eye } from 'lucide-react';
import CivicModal from './CivicModal';
import { ModalBlock } from '../lib/supabase/modalContent';
import { ActionModalBlock } from '../lib/actionModalBlockTypes';
import { ActionModalBlockRenderer } from './ActionModalBlockRenderer';
import { useExplorer } from '../lib/ExplorerContext';
import { useAuth } from '../lib/AuthContext';
import { fetchTimelineEvents, type TimelineEvent } from '../lib/explorerTimelineService';
import ExplorerTimeline from './ExplorerTimeline';

interface LookDeeperModalUnifiedProps {
  isOpen: boolean;
  onClose: () => void;
  system: 'legacy' | 'new';
  legacyBlocks?: ModalBlock[];
  newBlocks?: ActionModalBlock[];
  songTitle: string;
  civicLevel?: string;
  songId?: string;
}

export default function LookDeeperModalUnified({
  isOpen,
  onClose,
  system,
  legacyBlocks = [],
  newBlocks = [],
  songTitle,
  civicLevel,
  songId,
}: LookDeeperModalUnifiedProps) {
  const { user } = useAuth();
  const { lensFocus, loadLensFocus } = useExplorer();
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const isObserverLevel = civicLevel === 'Observer';
  const isExplorerLevel = civicLevel === 'Explorer';

  // Load timeline events for Observer level
  useEffect(() => {
    if (isOpen && isObserverLevel && songId) {
      loadTimelineEvents();
    }
  }, [isOpen, isObserverLevel, songId]);

  async function loadTimelineEvents() {
    setLoading(true);
    try {
      const events = await fetchTimelineEvents(songId!);
      setTimelineEvents(events);
    } catch (err) {
      console.error('[LookDeeperModalUnified] Error loading timeline:', err);
      setTimelineEvents([]);
    } finally {
      setLoading(false);
    }
  }

  // Load lens focus when modal opens for Explorer level
  useEffect(() => {
    if (isOpen && isExplorerLevel && songId && user) {
      loadLensFocus(songId);
    }
  }, [isOpen, isExplorerLevel, songId, user, loadLensFocus]);

  // Timeline rendering for Observer level
  const renderObserverTimeline = () => {
    return (
      <div className="w-full">
        {/* Timeline card */}
        <div
          className="w-full rounded-xl border shadow-lg"
          style={{
            backgroundColor: '#1A1A1A',
            borderColor: 'rgba(255, 255, 255, 0.12)',
            maxHeight: '480px',
            overflowY: 'auto',
          }}
        >
          <div className="p-4 md:p-5">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div
                  className="w-8 h-8 border-4 rounded-full animate-spin"
                  style={{
                    borderColor: '#40E0D0',
                    borderTopColor: 'transparent',
                  }}
                ></div>
              </div>
            ) : (
              <ExplorerTimeline events={timelineEvents} />
            )}
          </div>
        </div>
      </div>
    );
  };

  // New system rendering (other levels)
  const renderNewBlocks = () => {
    if (newBlocks.length === 0) {
      return (
        <div>
          <p className="text-[14px] text-[#E0E0E0]" style={{ lineHeight: '1.6' }}>
            No content available for this level yet.
          </p>
        </div>
      );
    }

    return (
      <div>
        {newBlocks.map((block, index) => (
          <div key={block.id || index}>
            <ActionModalBlockRenderer block={block} />
          </div>
        ))}
      </div>
    );
  };

  const getCategoryDisplayName = (category: string): string => {
    const map: Record<string, string> = {
      power: 'Power',
      justice: 'Justice',
      systems: 'Systems',
      identity: 'Identity',
      resistance: 'Resistance',
      institutions: 'Institutions',
      visibility: 'Visibility',
      collective_action: 'Collective Action',
      historical_patterns: 'Historical Patterns',
      structural_change: 'Structural Change',
    };
    return map[category] || category;
  };

  return (
    <CivicModal
      isOpen={isOpen}
      onClose={onClose}
      title="Look Deeper"
      subtitle={isObserverLevel ? "Tap a moment to see how this story echoes across time" : undefined}
      icon={Eye}
      maxWidth="lg"
    >
      {isObserverLevel ? (
        renderObserverTimeline()
      ) : (
        <div
          className="bg-[#1A1A1A] rounded-xl border"
          style={{
            borderColor: 'rgba(255, 255, 255, 0.08)',
            padding: '32px 24px 20px 24px',
          }}
        >
          {/* Lens Callout Banner - Explorer Level Only */}
          {isExplorerLevel && lensFocus && (
            <div
              className="mb-6 p-3 rounded-lg border border-[#40E0D0]/30 bg-[#40E0D0]/5"
            >
              <div className="flex items-start gap-2">
                <div className="mt-0.5">
                  <Eye className="w-4 h-4 text-[#40E0D0]" />
                </div>
                <div>
                  <div className="text-[12px] font-semibold text-[#40E0D0] uppercase tracking-wide mb-1">
                    Lens: {getCategoryDisplayName(lensFocus.lens_category)}
                  </div>
                  <p className="text-[11px] text-[#B0B0B0] leading-relaxed">
                    Viewing this content through the {getCategoryDisplayName(lensFocus.lens_category).toLowerCase()} lens
                  </p>
                </div>
              </div>
            </div>
          )}

          {renderNewBlocks()}
        </div>
      )}
    </CivicModal>
  );
}
